import { useCallback, useEffect, useRef } from "react";
import toast from "react-hot-toast";

import { notify } from "../utils/notification";
import { tomorrowCursor } from "../utils/helpers";

// Mensagem já presente (mesmo id, ou mesma chave do provedor quando o evento
// do WhatsApp chega depois do envio pelo CRM): não duplica na tela.
const hasMessage = (list, msg) =>
  list.some(
    (message) =>
      message.messageId === msg.messageId ||
      (msg.providerMessageKey && message.providerMessageKey === msg.providerMessageKey),
  );

/**
 * Aplica na lista de conversas e na conversa aberta os eventos do WebSocket:
 * mensagens novas, leitura, agendamentos, transcrição de áudio, notas e
 * mensagens antigas carregadas. Também avisa (notificação do navegador) a
 * mensagem recebida com a aba em segundo plano.
 */
export function useConversationEvents({
  messages,
  conversations,
  setConversations,
  selectedChat,
  setSelectedChat,
  fetchConversations,
  userId,
  // Conversa nova criada por este atendente pelo "Nova conversa".
  onConversationStartedByMe,
}) {
  const lastNotifiedMessageId = useRef(null);
  const lastScheduledToastRef = useRef(null);
  const loadedOlderRef = useRef(new Set());
  const connectionEstablishedRef = useRef(false);
  const lastProcessedEventIdRef = useRef(null);

  const handleMessageSchedulingUpdate = useCallback(
    (scheduledMsg) => {
      const conversationId = scheduledMsg.conversationId;

      const upsert = (conv) => {
        const index = conv.scheduledMessages?.findIndex((m) => m.id === scheduledMsg.id);

        if (index !== -1) {
          const updated = [...conv.scheduledMessages];
          updated[index] = scheduledMsg;
          return { ...conv, scheduledMessages: updated };
        }

        return {
          ...conv,
          scheduledMessages: [...(conv.scheduledMessages || []), scheduledMsg],
        };
      };

      setConversations((prev) =>
        prev.map((conv) => (conv.id === conversationId ? upsert(conv) : conv)),
      );
      setSelectedChat((prev) => (!prev || prev.id !== conversationId ? prev : upsert(prev)));
    },
    [setConversations, setSelectedChat],
  );

  const handleTranscriptUpdate = useCallback(
    ({ msgId, transcription }) => {
      const apply = (msg) => (msg.id === msgId ? { ...msg, messageBody: transcription } : msg);

      setConversations((prev) =>
        prev.map((conv) => ({ ...conv, messages: conv.messages?.map(apply) })),
      );
      setSelectedChat((prev) => (prev ? { ...prev, messages: prev.messages.map(apply) } : prev));
    },
    [setConversations, setSelectedChat],
  );

  const handleIncomingNotification = useCallback(
    (msg) => {
      if (!msg) return;
      if (msg.direction !== "INCOMING") return;

      if (lastNotifiedMessageId.current === msg.messageId) return;
      lastNotifiedMessageId.current = msg.messageId;

      const isOpen = selectedChat?.id === msg.conversationId && !document.hidden;
      const isTabHidden = document.visibilityState !== "visible";

      if (isOpen || !isTabHidden) return;

      const conversation = conversations.find((c) => c.id === msg.conversationId);

      if (!conversation) return;

      notify({
        title: `Nova mensagem de ${conversation.contact.name}`,
        body: msg.messageType === "text" ? msg.messageBody.slice(0, 80) : "Nova mensagem",
        onClick: () => {
          setSelectedChat(conversation);
        },
      });
    },
    [selectedChat, conversations, setSelectedChat],
  );

  // Mensagens antigas pedidas pelo "Carregar mensagens anteriores".
  useEffect(() => {
    if (!messages.length) return;

    const last = messages[0];
    if (last.type !== "loaded_older_messages") return;
    if (!selectedChat) return;

    if (last.conversationId !== selectedChat.id) return;

    const batchKey = last.conversationId + "-" + (last.result?.[0]?.messageId || "empty");

    if (loadedOlderRef.current.has(batchKey)) return;
    loadedOlderRef.current.add(batchKey);

    const olderMessages = last.result || [];
    const mergeMessages = (current = []) => {
      const byId = new Map();

      for (const message of [...olderMessages, ...current]) {
        byId.set(message.messageId || message.id, message);
      }

      return [...byId.values()].sort((a, b) => new Date(a.sentAt) - new Date(b.sentAt));
    };

    setSelectedChat((prev) => ({
      ...prev,
      messages: mergeMessages(prev.messages),
    }));

    setConversations((prev) =>
      prev.map((conv) =>
        conv.id === last.conversationId ? { ...conv, messages: mergeMessages(conv.messages) } : conv,
      ),
    );
  }, [messages, selectedChat, setConversations, setSelectedChat]);

  useEffect(() => {
    if (!messages.length) return;

    const lastMessage = messages[0];
    if (lastProcessedEventIdRef.current === lastMessage.id) return;
    lastProcessedEventIdRef.current = lastMessage.id;

    switch (lastMessage.type) {
      // Reconexão do WebSocket: recarrega a lista para recuperar o que chegou
      // enquanto a tela estava desconectada (a primeira conexão não conta).
      case "connection_established": {
        if (!connectionEstablishedRef.current) {
          connectionEstablishedRef.current = true;
          return;
        }

        fetchConversations(tomorrowCursor()).then((freshConversations) => {
          if (!freshConversations) return;

          setSelectedChat((current) =>
            current
              ? freshConversations.find((conversation) => conversation.id === current.id) || current
              : current,
          );
        });
        return;
      }

      case "scheduled_message_added":
        handleMessageSchedulingUpdate(lastMessage.result);

        if (lastScheduledToastRef.current !== lastMessage.result?.id) {
          lastScheduledToastRef.current = lastMessage.result?.id;
          toast.success("Mensagem agendada com sucesso!");
        }
        return;

      case "scheduled_message_status":
        handleMessageSchedulingUpdate(lastMessage.result);
        return;

      case "transcripted_audio":
        handleTranscriptUpdate(lastMessage.result);
        return;

      case "whatsapp_message_read": {
        const readUpdate = lastMessage.result;
        if (!readUpdate?.conversationId) return;

        const applyReadUpdate = (conversation) => {
          if (conversation.id !== readUpdate.conversationId) return conversation;

          return {
            ...conversation,
            messages: (conversation.messages || []).map((message) =>
              message.direction === readUpdate.direction ? { ...message, isRead: true } : message,
            ),
          };
        };

        setConversations((prev) => prev.map(applyReadUpdate));
        setSelectedChat((prev) => (prev ? applyReadUpdate(prev) : prev));
        return;
      }

      // Resumo de ligação salvo pelo backend: atualiza a lista de notas aberta,
      // para a próxima nota do atendente não gravar por cima de uma lista velha.
      case "conversation_notes_updated": {
        const { conversationId, notes } = lastMessage.result || {};
        if (!conversationId) return;
        const applyNotes = (conversation) =>
          conversation.id === conversationId ? { ...conversation, notes } : conversation;
        setConversations((prev) => prev.map(applyNotes));
        setSelectedChat((prev) => (prev ? applyNotes(prev) : prev));
        return;
      }

      case "whatsapp_message_sent":
      case "whatsapp_message_received": {
        if (!lastMessage.result) return;
        const { messageCount, isNewConversation } = lastMessage.result;

        if (isNewConversation) {
          const newConv = lastMessage.result;
          const initiatedByCurrentCrm =
            lastMessage.type === "whatsapp_message_sent" &&
            lastMessage.source === "crm_send" &&
            String(lastMessage.initiatedByUserId) === String(userId);

          setConversations((prev) => [
            newConv,
            ...prev.filter((conversation) => conversation.id !== newConv.id),
          ]);

          setSelectedChat((prev) =>
            initiatedByCurrentCrm || prev?.id === newConv.id ? newConv : prev,
          );

          if (initiatedByCurrentCrm) onConversationStartedByMe?.();

          handleIncomingNotification(newConv.messages?.[newConv.messages.length - 1]);
          return;
        }

        const { msg, unreadCount, lastMessageAt } = lastMessage.result;
        if (!msg) return;

        const applyMessage = (conv) => ({
          ...conv,
          messageCount: Math.max(conv.messageCount, messageCount),
          lastMessageAt,
          _count: { ...conv._count, messages: unreadCount },
          messages: hasMessage(conv.messages, msg) ? conv.messages : [...conv.messages, msg],
        });

        // A conversa com mensagem nova sobe para o topo da lista.
        setConversations((prev) => {
          const next = [];
          let updatedConv = null;

          for (const conv of prev) {
            if (conv.id === msg.conversationId) updatedConv = applyMessage(conv);
            else next.push(conv);
          }

          return updatedConv ? [updatedConv, ...next] : prev;
        });

        setSelectedChat((prev) => (prev?.id === msg.conversationId ? applyMessage(prev) : prev));

        handleIncomingNotification(msg);
        return;
      }

      default:
        return;
    }
  }, [
    fetchConversations,
    handleIncomingNotification,
    handleMessageSchedulingUpdate,
    handleTranscriptUpdate,
    messages,
    onConversationStartedByMe,
    setConversations,
    setSelectedChat,
    userId,
  ]);
}
